<template>
  <!-- The AI AutoPilot chat on the feature stage backdrop (the dot grid and plan glow of
       components/FeatureMotion.vue). With `autoplay`, it plays ask → work → approve → done once
       when it scrolls into view, then rests on `done` with a Replay button (well inside 10 s,
       WCAG 2.2.2). Without `autoplay`, `state` drives it (the sticky chapters). The server and
       reduce-motion render the resting `done` frame. -->
  <div ref="rootEl" class="scs" :class="{ 'scs--tall': tall }">
    <div class="scs-frame">
      <MotionAiChatWindow :state="current" />
    </div>
    <button v-show="canReplay" type="button" class="scs-replay" aria-label="Replay preview" title="Replay preview" @click="play">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" /></svg>
    </button>
  </div>
</template>

<script setup>
const props = defineProps({
  autoplay: { type: Boolean, default: false },
  state: { type: String, default: "done" },
  tall: { type: Boolean, default: false },
  // With `autoplay`, the screen it plays to and rests on (the server renders it too).
  rest: { type: String, default: "done" },
});

const order = ["ask", "work", "approve", "done"];
// How long each screen holds while playing (ms): typing needs the longest.
const holds = { ask: 2300, work: 2100, approve: 2300 };

const rootEl = ref(null);
const playing = ref(null);
const resting = ref(false);
let timers = [];
let observer = null;

const current = computed(() => (props.autoplay ? playing.value ?? props.rest : props.state));
const canReplay = computed(() => props.autoplay && resting.value);

const clear = () => {
  timers.forEach(clearTimeout);
  timers = [];
};
const play = () => {
  clear();
  resting.value = false;
  let at = 0;
  order.slice(0, order.indexOf(props.rest) + 1).forEach((step) => {
    timers.push(setTimeout(() => { playing.value = step; }, at));
    at += holds[step] ?? 0;
  });
  timers.push(setTimeout(() => { resting.value = true; }, at + 600));
};

onMounted(() => {
  if (!props.autoplay) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  observer = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    observer.disconnect();
    play();
  }, { threshold: 0.35 });
  observer.observe(rootEl.value);
});

onBeforeUnmount(() => {
  clear();
  observer?.disconnect();
});
</script>

<style>
.scs {
  --scs-p1: 124 155 224;
  --scs-p2: 159 139 230;
  --scs-dot: rgb(0 0 0 / 0.1);
  position: relative;
  height: 470px;
  border-radius: 14px;
  background-color: rgb(0 0 0 / 0.018);
  isolation: isolate;
}
.scs--tall { height: 520px; }
.scs::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background:
    radial-gradient(circle, var(--scs-dot) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    radial-gradient(150% 150% at 50% 45%, rgb(var(--scs-p1) / 0.16) 0%, rgb(var(--scs-p2) / 0.08) 50%, rgb(var(--scs-p2) / 0) 100%);
  -webkit-mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
  mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
}
.dark .scs { --scs-dot: rgb(255 255 255 / 0.13); background-color: rgb(255 255 255 / 0.03); }
.scs-frame { height: 100%; max-width: 520px; margin: 0 auto; padding: 28px 28px 30px; }
.scs-replay {
  position: absolute;
  right: 8px;
  bottom: 4px;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #595f68;
  cursor: pointer;
}
.dark .scs-replay { color: #a3a3a3; }
.scs-replay:hover { background: rgb(0 0 0 / 0.08); }
.scs-replay:focus-visible { outline: 2px solid #0e7490; outline-offset: 1px; }
.scs-replay svg { width: 20px; height: 20px; fill: currentColor; }
@media (max-width: 639px) {
  .scs, .scs--tall { height: 500px; }
  .scs-frame { padding: 14px 12px 44px; }
}
</style>
